import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { StickyNote, Save, Trash2 } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { BoldTextarea } from "@/components/BoldTextarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export const NoteField = ({ value, onChange, label = "Note (tampil di PDF)", placeholder, testId }) => {
  const [tpls, setTpls] = useState([]);
  const [saveOpen, setSaveOpen] = useState(false);
  const [name, setName] = useState("");
  const load = useCallback(() => api.get("/note-templates").then((r) => setTpls(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const apply = (id) => {
    const t = tpls.find((x) => x.id === id);
    if (!t) return;
    if (!value?.trim()) return onChange(t.content);
    const add = window.confirm(`Note sudah berisi teks.\nOK = tambahkan "${t.name}" di bawahnya\nCancel = ganti seluruh Note dengan template`);
    onChange(add ? `${value.trimEnd()}\n\n${t.content}` : t.content);
  };
  const saveTpl = async () => {
    try {
      await api.post("/note-templates", { name, content: value });
      toast.success(`Template "${name}" tersimpan`);
      setSaveOpen(false); setName(""); load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const del = async (t) => {
    if (!window.confirm(`Hapus template "${t.name}"?`)) return;
    try { await api.delete(`/note-templates/${t.id}`); toast.success("Template dihapus"); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="space-y-2" data-testid={`${testId}-field`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</span>
        <div className="flex flex-wrap items-center gap-2">
          <StickyNote className="h-4 w-4 text-amber-600" />
          <select className="h-8 min-w-[200px] rounded-md border border-input bg-white px-2 text-xs" value="" onChange={(e) => apply(e.target.value)} data-testid={`${testId}-template-select`}>
            <option value="">— Pakai Template Note —</option>
            {tpls.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
          <Button type="button" size="sm" variant="outline" className="h-8 bg-white text-xs" disabled={!value?.trim()} onClick={() => setSaveOpen(true)} data-testid={`${testId}-save-template-btn`}><Save className="h-3.5 w-3.5 mr-1" />Simpan sbg Template</Button>
        </div>
      </div>
      <BoldTextarea rows={Math.min(Math.max((value || "").split("\n").length, 3), 16)} value={value} onChange={onChange} placeholder={placeholder} testId={testId} />
      <Dialog open={saveOpen} onOpenChange={setSaveOpen}>
        <DialogContent aria-describedby={undefined} className="max-w-lg">
          <DialogHeader><DialogTitle className="font-heading">Simpan Note sebagai Template</DialogTitle></DialogHeader>
          <Input placeholder="Nama template, contoh: Spesifikasi Server VMS 1000 Camera" value={name} onChange={(e) => setName(e.target.value)} data-testid={`${testId}-template-name-input`} />
          <pre className="max-h-48 overflow-auto whitespace-pre-wrap rounded-md bg-slate-50 p-2 text-xs text-slate-600">{value}</pre>
          <Button disabled={!name.trim()} onClick={saveTpl} data-testid={`${testId}-template-confirm-btn`}>Simpan Template</Button>
          {!!tpls.length && (
            <div className="border-t pt-3">
              <p className="mb-2 text-xs font-semibold text-slate-500">Template tersimpan</p>
              <ul className="max-h-40 space-y-1 overflow-auto text-sm">
                {tpls.map((t, i) => <li key={t.id} className="flex items-center justify-between gap-2"><span>{t.name} <span className="text-[11px] text-slate-400">• {t.created_by}</span></span><Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => del(t)} data-testid={`${testId}-template-delete-${i}`}><Trash2 className="h-3.5 w-3.5 text-rose-500" /></Button></li>)}
              </ul>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
